import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ReportFileType, ReportStatus, SalesReportViewType } from 'src/app/enum';
import { ODataResponse } from './data-table.service';
import { ITenantSimpleDto } from '../models/ITenantSimpleDto';
import { IUser } from '../models';

export interface ILocationSimple {
  id: string;
  name?: string;
  address?: string;
  email?: string;
  gln?: string;
}

export interface ISalesReportFormData {
  minReportDate?: Date;
  maxReportDate?: Date;
  fileTypes?: ReportFileType[];
  locations?: ILocationSimple[];
}

export interface ISalesReportSelectedFilters {
  from?: Date;
  to?: Date;
  fileTypes?: ReportFileType[];
  locations?: ILocationSimple[];
}

export interface ISalesReportListItem {
  id: string;
  providerId: string;
  providerName: string;
  createdById: string;
  createdByFullName: string;
  createdByEmail: string;
  modifiedById: string;
  modifiedByFullName: string;
  modifiedByEmail: string;
  createdAt: Date;
  modifiedAt: Date;
  expiresAt: Date;
  status: ReportStatus;
  reportDateFrom?: Date;
  reportDateTo?: Date;
  fileTypes?: ReportFileType[];
  locationIds?: string[];
  locations?: ILocationSimple[];
}

export interface ISalesReportDto {
  id: string;
  status: ReportStatus;
  createdAt: Date;
  modifiedAt: Date;
  expiresAt: Date;
  provider: ITenantSimpleDto;
  createdBy: IUser;
  modifiedBy: IUser;
  formData: ISalesReportFormData;
  selectedDataFilters?: ISalesReportSelectedFilters;
}

export interface ICreateSalesReportRequest {
  providerId: string;
  from?: string;
  to?: string;
  fileTypes?: ReportFileType[];
  locationIds?: string[];
}

export interface IUpdateSalesReportRequest {
  id: string;
  from?: string;
  to?: string;
  fileTypes?: ReportFileType[];
  locationIds?: string[] | null;
}

export interface IGetSalesReportEntriesRequest {
  id: string;
  viewType: SalesReportViewType;
  from?: Date;
  to?: Date;
  locationIds?: string[];
  productIds?: string[];
  fileTypes?: ReportFileType[];
  searchTerm?: string;

  searchKey?: string;
  inventoryFilter?: number;
  priceType?: number;
}

export interface IExportSalesReportRequest extends IGetSalesReportEntriesRequest {
  orderBy?: string;
}

export interface ISalesReportDayMetric {
  date?: string;
  soldQuantity?: number;
  totalRevenue?: number;
  actualQuantity?: number;
  unitType?: string;
}

export interface ISalesReportRow {
  id: string;
  name: string;
  metrics: { [date: string]: ISalesReportDayMetric };

  productId?: string;
  productFullName?: string;
  productPrimaryEan?: string;
  productPrimaryCode?: string;
  productGs1SegmentId?: string;
  productGs1SegmentCode?: string;
  productGs1SegmentName?: string;

  locationId?: string;
  locationName?: string;
  locationGln?: string;
  locationFormat?: { [lang: string]: string };
}

export interface ISalesReportSearchData {
  searchTerm?: string;
  from?: Date;
  to?: Date;
  viewType?: SalesReportViewType;
  fileTypes?: ReportFileType[];
  locationIds?: string[];
  productIds?: string[];

  locations?: ILocationSimple[];
  productLabel?: string;

  inventoryFilter?: number;
  priceType?: number;
}

export enum SalesReportInventoryFilter {
  All = 1,
  CriticalOnly = 2
}

export enum SalesReportPriceType {
  Final = 1,
  Purchase = 2
}

@Injectable()
export class ReportsService {
  constructor(private _http: HttpClient) { }

  public getSalesReportFormData(providerId: string): Observable<ISalesReportFormData> {
    return this._http.get<ISalesReportFormData>(
      `api/Reports/SalesReport/${providerId}/FormData`
    );
  }

  public listSalesReports(params?: HttpParams): Observable<ODataResponse<ISalesReportListItem>> {
    return this._http.get<ODataResponse<ISalesReportListItem>>('api/Reports/SalesReport', {
      params
    });
  }

  public createSalesReport(request: ICreateSalesReportRequest): Observable<string> {
    return this._http.post<string>('api/Reports/SalesReport', request);
  }

  public getSalesReport(id: string): Observable<ISalesReportDto> {
    return this._http.get<ISalesReportDto>(`api/Reports/SalesReport/${id}`);
  }

  public updateSalesReport(id: string, request: IUpdateSalesReportRequest): Observable<string> {
    return this._http.post<string>(`api/Reports/SalesReport/${id}`, request);
  }

  public getSalesReportEntries(
    id: string,
    request: IGetSalesReportEntriesRequest,
    params?: HttpParams
  ): Observable<ODataResponse<ISalesReportRow>> {
    return this._http.post<ODataResponse<ISalesReportRow>>(
      `api/Reports/SalesReport/${id}/entries`,
      request,
      { params }
    );
  }

  public exportXls(id: string, request: IExportSalesReportRequest) {
    return this._http.post<{ id: string; fileName: string }>(
      `api/Reports/SalesReport/${id}/exportXls`,
      request
    );
  }

  public downloadXls(fileId: string) {
    return this._http.get(`api/Reports/downloadXls/${fileId}`, {
      responseType: 'blob'
    });
  }
}
